<template>
  <div class="routine-panel">
    <div class="routine-panel__head">
      <div class="routine-panel__title-row">
        <span class="routine-panel__title">Routines</span>
        <span class="routine-scope">
          <span :class="{ 'is-on': scope==='mine' }" @click="setScope('mine')">Mine</span>
          <span :class="{ 'is-on': scope==='workspace' }" @click="setScope('workspace')">Workspace</span>
        </span>
      </div>
      <div class="routine-panel__actions">
        <input class="fts-form-control routine-search" v-model="search" placeholder="Search" />
        <fts-btn text icon :disabled="loading" @click="load"><font-awesome-icon :icon="['fas','rotate']" /></fts-btn>
        <fts-btn raised color="primary" @click="openNew"><font-awesome-icon :icon="['fas','plus']" /> New routine</fts-btn>
      </div>
    </div>

    <div v-if="!loading && filtered.length === 0" class="routine-empty">
      No routines yet — create one to get started.
    </div>

    <div v-for="r in filtered" :key="r.id" class="routine-row-wrap">
      <div class="routine-row">
        <span class="routine-row__icon"><font-awesome-icon :icon="iconFor(r)" /></span>
        <!-- a Studio schedule's routine is read-only here (docs/scheduling-routine-automation-proposal.md R-8):
             it is edited, switched and run from its module's automations -->
        <div class="routine-row__main" :class="{ 'is-readonly': isStudio(r) }" @click="!isStudio(r) && openEdit(r)">
          <div class="routine-row__name">{{ r.name }}</div>
          <div class="routine-row__meta">{{ schedule(r) }}<span v-if="targetSummary(r)"> · {{ targetSummary(r) }}</span><span v-if="isStudio(r)"> · a module's schedule: edit it in Studio</span></div>
        </div>
        <span class="routine-badge is-studio" v-if="isStudio(r)">Studio</span>
        <span class="routine-badge" :class="r.enabled ? 'is-on' : 'is-off'">{{ r.enabled ? 'Enabled' : 'Disabled' }}</span>
        <div class="routine-row__runs">
          <div v-if="r.nextRunAt">Next: {{ fmtDate(r.nextRunAt) }}</div>
          <div v-if="r.lastRunAt" class="muted">Last: {{ fmtDate(r.lastRunAt) }}</div>
        </div>
        <div class="routine-row__cta">
          <template v-if="!isStudio(r)">
            <a class="fts-link-primary" @click="run(r)">Run now</a>
            <fts-toggle-switch :value="r.enabled" :size="18" @change="toggle(r)" />
          </template>
          <font-awesome-icon :icon="['fas','clock-rotate-left']" class="routine-history-btn" @click="toggleHistory(r)" />
        </div>
      </div>

      <div v-if="expandedId === r.id" class="routine-history">
        <div v-if="!runs.length" class="routine-history__empty">No runs yet.</div>
        <div v-for="run in runs" :key="run.id" class="routine-history__row">
          <span class="routine-run-status" :class="runClass(run.status)">{{ runText(run.status) }}</span>
          <div class="routine-history__main">
            <div>{{ fmtDate(run.startedAt) }}</div>
            <div class="muted">{{ run.summary || '—' }}</div>
          </div>
          <span class="muted">{{ run.durationMs ? (run.durationMs/1000).toFixed(1) + 's' : '' }}</span>
        </div>
      </div>
    </div>

    <fts-routine-design-modal :visible="showDesigner" :value="editing" :modules="modules" @close="showDesigner=false" @saved="onSaved" />
  </div>
</template>

<script>
import { routineStore } from "../store/routineStore";
import { formatSchedule } from "../commons/formatSchedule";
import FTSRoutineDesignModal from "./FTSRoutineDesignModal.vue";
import FTSBtn from "../elements/FTSBtn";
import FTSToggleSwitch from "../elements/FTSToggleSwitch";

const RUN_STATUS = ["Running", "Succeeded", "Partially failed", "Failed", "Skipped", "Preview"];
const RUN_CLASS = {
  Succeeded: "rs-success", "Partially failed": "rs-warning", Failed: "rs-danger",
  Skipped: "rs-teal", Preview: "rs-accent", Running: "rs-teal",
};

export default {
  name: "FTSRoutinePanel",
  components: { "fts-routine-design-modal": FTSRoutineDesignModal, "fts-btn": FTSBtn, "fts-toggle-switch": FTSToggleSwitch },
  data() {
    return {
      scope: "mine", search: "", loading: false,
      showDesigner: false, editing: null,
      expandedId: null, runs: [],
    };
  },
  computed: {
    routines() { return routineStore.state.routines; },
    modules() {
      return (this.$store && this.$store.state && this.$store.state.workspaceModules) || [];
    },
    filtered() {
      const q = (this.search || "").toLowerCase();
      return this.routines.filter(r => !q || (r.name || "").toLowerCase().indexOf(q) >= 0);
    },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true;
      try { await routineStore.dispatch("loadRoutines", { scope: this.scope }); }
      catch (e) { /* noop */ }
      finally { this.loading = false; }
      this.openFromQuery();
    },
    /**
     * ?routine=<id> (the notification reader's "Open routine") lands on that routine with its
     * run history expanded. Personal routines live under the other scope — switch once.
     */
    openFromQuery() {
      const id = this.$route && this.$route.query && this.$route.query.routine;
      if (!id || this._queryOpened) return;
      const r = this.routines.find(x => String(x.id) === String(id));
      if (r) {
        this._queryOpened = true;
        this.toggleHistory(r);
      } else if (!this._queryScopeTried) {
        this._queryScopeTried = true;
        this.setScope(this.scope === "Workspace" ? "Personal" : "Workspace");
      }
    },
    setScope(s) { if (this.scope !== s) { this.scope = s; this.load(); } },
    openNew() { this.editing = null; this.showDesigner = true; },
    openEdit(r) { this.editing = r; this.showDesigner = true; },
    onSaved() { this.showDesigner = false; this.load(); },
    async toggle(r) { try { await routineStore.dispatch("toggleRoutine", r); } catch (e) {} this.load(); },
    async run(r) { try { await routineStore.dispatch("runRoutine", { id: r.id, dryRun: false }); this.load(); } catch (e) {} },
    async toggleHistory(r) {
      if (this.expandedId === r.id) { this.expandedId = null; return; }
      this.expandedId = r.id; this.runs = [];
      try { this.runs = await routineStore.dispatch("loadRuns", r.id); } catch (e) {}
    },
    schedule(r) { return formatSchedule(r); },
    /* Routine.Source: 0 User, 1 Canvas (a Studio schedule's routine; the API refuses edits to it) */
    isStudio(r) { return r.source === 1 || r.source === "Canvas"; },
    targetSummary(r) {
      const n = (r.targets || []).length;
      return n ? (n + (n === 1 ? " module" : " modules")) : "";
    },
    iconFor(r) { return r.contentMode === "Teammate" ? ["fas", "robot"] : ["far", "bell"]; },
    fmtDate(d) {
      if (!d) return "";
      try { return new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
      catch (e) { return d; }
    },
    normStatus(s) { return typeof s === "number" ? (RUN_STATUS[s] || String(s)) : s; },
    runText(s) { return this.normStatus(s); },
    runClass(s) { return RUN_CLASS[this.normStatus(s)] || "rs-teal"; },
  },
};
</script>

<style scoped>
.routine-panel { padding: 1.25rem; max-width: 900px; margin: 0 auto; }
.routine-panel__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem; flex-wrap: wrap; gap: 8px; }
.routine-panel__title-row { display: flex; align-items: center; gap: 12px; }
.routine-panel__title { font-size: 18px; font-weight: 500; }
.routine-scope { display: inline-flex; gap: 4px; font-size: 13px; }
.routine-scope span { padding: 3px 12px; border-radius: 999px; color: #888; cursor: pointer; }
.routine-scope span.is-on { background: #e6f1fb; color: #185fa5; }
.routine-panel__actions { display: flex; align-items: center; gap: 8px; }
.fts-form-control { padding: 8px 10px; border: 1px solid #ddd; border-radius: 6px; font-size: 13px; box-sizing: border-box; }
.routine-search { width: 180px; }
.routine-empty { padding: 2.5rem; text-align: center; color: #999; font-size: 14px; }
.routine-row-wrap { border-top: 1px solid #eee; }
.routine-row { display: flex; align-items: center; gap: 12px; padding: 12px 0; }
.routine-row__icon { width: 34px; height: 34px; border-radius: 8px; background: #e6f1fb; color: #185fa5; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.routine-row__main { flex: 1; min-width: 0; cursor: pointer; }
.routine-row__name { font-size: 14px; font-weight: 500; }
.routine-row__meta { font-size: 12px; color: #888; }
.routine-badge { font-size: 11px; padding: 3px 9px; border-radius: 999px; }
.routine-badge.is-on { background: #e8f5e9; color: #2e7d32; }
.routine-badge.is-off { background: #f2f2f2; color: #999; }
.routine-badge.is-studio { background: #e6f1fb; color: #185fa5; }
.routine-row__main.is-readonly { cursor: default; }
.routine-row__runs { min-width: 110px; text-align: right; font-size: 11px; color: #666; }
.routine-row__cta { display: flex; align-items: center; gap: 12px; }
.fts-link-primary { color: #185fa5; cursor: pointer; font-size: 12px; }
.routine-history-btn { color: #999; cursor: pointer; }
.muted { color: #aaa; }
.routine-history { background: #fafafa; border-radius: 8px; padding: 6px 12px 10px; margin: 0 0 10px 46px; }
.routine-history__empty { font-size: 12px; color: #999; padding: 8px 0; }
.routine-history__row { display: flex; align-items: center; gap: 12px; padding: 8px 0; border-bottom: 1px solid #eee; }
.routine-history__row:last-child { border-bottom: none; }
.routine-history__main { flex: 1; font-size: 12px; }
.routine-run-status { font-size: 11px; padding: 3px 9px; border-radius: 999px; min-width: 82px; text-align: center; }
.rs-success { background: #e8f5e9; color: #2e7d32; }
.rs-warning { background: #fff3e0; color: #e65100; }
.rs-danger { background: #ffebee; color: #c62828; }
.rs-teal { background: #e0f2f1; color: #00695c; }
.rs-accent { background: #e6f1fb; color: #185fa5; }
</style>
